import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Button, ButtonType } from '../../../../components/Button';
import { ModalBase } from './types';

// -- Rates Edit Modal ------------------------------------------------------
const pluralizeKidsLabel = (n: number): string =>
  n === 1 ? '1 child, senior, or pet' : `${n} children, seniors, or pets`;

const MINIMUM_HOUR_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'None' },
  { value: 2, label: '2 hours' },
  { value: 3, label: '3 hours' },
  { value: 4, label: '4 hours' },
  { value: 5, label: '5 hours' },
  { value: 6, label: '6 hours' },
  { value: 8, label: '8 hours' },
];

export const RatesEditModal = ({
  isOpen,
  onClose,
  initialRates,
  initialMaxKids,
  initialMinimumHours,
  initialAcceptsFixedRates,
  initialWillSitSingleFamily,
  suggestedRate,
  onSave,
}: ModalBase & {
  initialRates: Array<{ minimumRate?: number; maximumRate?: number }>;
  initialMaxKids?: number;
  initialMinimumHours?: number;
  initialAcceptsFixedRates?: boolean;
  initialWillSitSingleFamily?: boolean;
  suggestedRate?: { rate?: number; defaultRate?: boolean };
  onSave: (patch: {
    rates: Array<{ minimumRate: number; maximumRate: number }>;
    maxKids?: number;
    minimumHours?: number;
    acceptsFixedRates?: boolean;
    willSitSingleFamily?: boolean;
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [rates, setRates] = useState(() =>
    [0, 1, 2, 3].map((i) => ({
      minimumRate: String(initialRates[i]?.minimumRate ?? ''),
      maximumRate: String(initialRates[i]?.maximumRate ?? ''),
    })),
  );
  const [maxKids, setMaxKids] = useState(String(initialMaxKids ?? ''));
  const [minimumHours, setMinimumHours] = useState(String(initialMinimumHours ?? ''));
  const [acceptsFixedRates, setAcceptsFixedRates] = useState(!!initialAcceptsFixedRates);
  const [willSitSingleFamily, setWillSitSingleFamily] = useState(!!initialWillSitSingleFamily);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setRates(
        [0, 1, 2, 3].map((i) => ({
          minimumRate: String(initialRates[i]?.minimumRate ?? ''),
          maximumRate: String(initialRates[i]?.maximumRate ?? ''),
        })),
      );
      setMaxKids(String(initialMaxKids ?? ''));
      setMinimumHours(String(initialMinimumHours ?? ''));
      setAcceptsFixedRates(!!initialAcceptsFixedRates);
      setWillSitSingleFamily(!!initialWillSitSingleFamily);
      setError('');
    }
  }, [isOpen, initialRates, initialMaxKids, initialMinimumHours, initialAcceptsFixedRates, initialWillSitSingleFamily]);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const cleaned = rates
        .map((r) => ({
          minimumRate: Number(r.minimumRate) || 0,
          maximumRate: Number(r.maximumRate) || Number(r.minimumRate) || 0,
        }))
        .filter((r) => r.minimumRate > 0);
      if (cleaned[0] && cleaned[0].minimumRate < 8) {
        setError('Minimum rate must be at least $8.');
        setSubmitting(false);
        return;
      }
      await onSave({
        rates: cleaned,
        maxKids: maxKids ? Number(maxKids) : undefined,
        minimumHours: minimumHours ? Number(minimumHours) : undefined,
        acceptsFixedRates,
        willSitSingleFamily,
      });
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editRates')}
      className='w-full max-w-3xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-sitter-rates'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
        <div>
          <h4 className='mb-1 font-semibold'>
            Rates by number of children, seniors, or pets:
          </h4>
          {suggestedRate?.rate != null && suggestedRate.rate > 0 && (
            <div className='mb-2'>
              {suggestedRate.defaultRate
                ? `On average, caregivers charge $${suggestedRate.rate}/hr`
                : `Caregivers in your area are charging $${suggestedRate.rate}/hr`}
            </div>
          )}
          <div className='mb-3 text-sm'>
            Based on our data, caregivers often start out with lower rates until they can
            build up their reviews and repeat families.
          </div>
          <div className='space-y-2'>
            {rates.map((r, i) => (
              <div key={i} className='flex items-center gap-2'>
                <span className='flex-1 text-sm'>{pluralizeKidsLabel(i + 1)}</span>
                <div className='flex items-center border border-neutral-300 rounded w-24 flex-shrink-0'>
                  <span className='px-2 bg-neutral-100'>$</span>
                  <input
                    id={`min-rate-${i + 1}`}
                    aria-label={`Minimum rate for ${i + 1}`}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={2}
                    className='w-full px-1 py-1 text-sm min-w-0'
                    value={r.minimumRate}
                    onChange={(e): void =>
                      setRates((prev) =>
                        prev.map((row, idx) =>
                          idx === i ? { ...row, minimumRate: e.target.value } : row,
                        ),
                      )
                    }
                  />
                </div>
                <span className='text-center flex-shrink-0'>-</span>
                <div className='flex items-center border border-neutral-300 rounded w-24 flex-shrink-0'>
                  <span className='px-2 bg-neutral-100'>$</span>
                  <input
                    id={`max-rate-${i + 1}`}
                    aria-label={`Maximum rate for ${i + 1}`}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={2}
                    className='w-full px-1 py-1 text-sm min-w-0'
                    value={r.maximumRate}
                    onChange={(e): void =>
                      setRates((prev) =>
                        prev.map((row, idx) =>
                          idx === i ? { ...row, maximumRate: e.target.value } : row,
                        ),
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className='mb-1 font-semibold'>Children, seniors, or pets maximum:</h4>
          <select
            id='max-num-children'
            aria-label={t('pages.sitterProfile.modals.maxChildrenAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-4'
            value={maxKids}
            onChange={(e): void => setMaxKids(e.target.value)}
          >
            <option value=''>Select</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>
                {pluralizeKidsLabel(n)}
              </option>
            ))}
          </select>

          <h4 className='mb-1 font-semibold'>Minimum hours per job</h4>
          <select
            id='minimum-hours'
            aria-label={t('pages.sitterProfile.modals.minHoursAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
            value={minimumHours}
            onChange={(e): void => setMinimumHours(e.target.value)}
          >
            {MINIMUM_HOUR_OPTIONS.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </select>

          <h4 className='mb-2 font-semibold'>
            Considerations for part-time or full-time jobs:
          </h4>
          <label className='flex items-start gap-2 mb-2 text-sm'>
            <input
              id='flat-rates-checkbox'
              type='checkbox'
              checked={acceptsFixedRates}
              onChange={(e): void => setAcceptsFixedRates(e.target.checked)}
            />
            Open to flat weekly/monthly rates
          </label>
          <label className='flex items-start gap-2 text-sm'>
            <input
              id='single-family-checkbox'
              type='checkbox'
              checked={willSitSingleFamily}
              onChange={(e): void => setWillSitSingleFamily(e.target.checked)}
            />
            Willing to work with one family exclusively, given a weekly hours commitment
          </label>
        </div>
      </div>

      {error && <p className='text-red-600 text-sm mt-3'>{error}</p>}
    </Modal>
  );
};
